import { useState } from "react"
import { bool, string, array, object } from "prop-types"
import { DynamicDataView } from "components/ui"
import { Icon } from "@planningcenter/tapestry-react"
import { pluralize } from "utils/helpers"
import { formatDateTimeShort } from "utils/dates"
import { api } from "utils/api"
import EventsPageHeader from "./EventsPageHeader"
import EmptyEvents from "./empty_events"
import EventStatusIndicator from "./EventStatusIndicator"
import EventCard from "./EventCard"
import ViewOnChurchCenterButton from "./ViewOnChurchCenterButton"
import StripeAccountNotificationBanner from "components/admin/StripeAccountNotificationBanner"

let fetchTimeout = null

const Events = ({
  archived: archivedFromProps,
  campuses,
  campusId,
  categories,
  categoryId,
  events: eventsFromProps,
  noLogoUrl,
  isAdmin = false,
  isOrgAdmin = false,
  isActingStaff = false,
  isStripeConnected = false,
  stripeConnectUrl = "",
  newEventUrl = "",
  orgHasEvents,
  churchCenterEventsUrl = "",
  ccwEnabled,
  ccaEnabled,
  ccaRegistrationsPublished,
  ccwRegistrationsPublished,
  churchCenterDisabledOrUnpublished,
  churchCenterEventSort,
  subscribedToPublishing,
  pagination: paginationFromProps,
  overageWarning,
  isPhoenix
}) => {
  const [events, setEvents] = useState(eventsFromProps)
  const [archived, setArchived] = useState(archivedFromProps === "1")
  const [searchQuery, setSearchQuery] = useState(
    window.location.search
      ?.substring(1)
      ?.split("&")
      ?.find(query => query.charAt(0) === "q")
      ?.split("=")[1] || ""
  )
  const [categoryFilter, setCategoryFilter] = useState(categoryId)
  const [campusFilter, setCampusFilter] = useState(campusId)
  const [pagination, setPagination] = useState(paginationFromProps)
  const [isLoading, setIsLoading] = useState(false)

  const fetchEvents = async ({ archived, searchQuery, category, campus, page }) => {
    const params = []

    if (archived) params.push("archived=1")
    if (searchQuery) params.push(`q=${searchQuery}`)
    if (category) params.push(`categories=${category}`)
    if (campus) params.push(`campuses=${campus}`)
    if (page) params.push(`page=${page}`)

    const paramString = params.join("&")
    const path = "/signups"

    if (paramString) {
      history.replaceState(history.state, "", `${path}?${paramString}`)
    } else {
      history.replaceState(history.state, "", path)
    }

    const url = `${path}.json?${paramString}`

    return api.get(url)
  }

  const debounceFetchEvents = props => {
    return new Promise((resolve, _) => {
      fetchTimeout = setTimeout(() => {
        resolve(fetchEvents(props))
      }, 800)
    })
  }

  const handleSearch = async ({ searchValue }) => {
    if (fetchTimeout) clearTimeout(fetchTimeout)

    setIsLoading(true)
    const q = searchValue.trim()
    setSearchQuery(q)
    const response = await debounceFetchEvents({
      searchQuery: q,
      archived,
      category: categoryFilter,
      campus: campusFilter,
      page: null
    })
    setEvents(response.events)
    setPagination(response.pagination)
    setIsLoading(false)
  }

  const handleFilter = async ({ scope }) => {
    const { campus, category } = scope
    const _archived = category === "archived"
    const _category = category !== "archived" ? (category === "all" ? null : category) : null
    const _campus = campus === "all" ? null : campus
    setIsLoading(true)
    setArchived(_archived)
    setCategoryFilter(_category)
    setCampusFilter(_campus)
    const response = await fetchEvents({
      archived: _archived,
      searchQuery,
      category: _category,
      campus: _campus,
      page: null
    })
    setEvents(response.events)
    setPagination(response.pagination)
    setIsLoading(false)
  }

  const handlePagination = async ({ page }) => {
    setIsLoading(true)
    const response = await fetchEvents({
      archived,
      searchQuery,
      category: categoryFilter,
      campus: campusFilter,
      page
    })
    setEvents(response.events)
    setPagination(response.pagination)
    setIsLoading(false)
  }

  const handleRowClick = (row, e) => {
    e.metaKey ? window.open(`/signups/${row.id}`, "_blank") : Turbolinks.visit(`/signups/${row.id}`)
  }

  const columns = [
    {
      cell: "logoUrl",
      render: ({ logoUrl }) => (
        <img
          alt="Signup"
          className="d-b"
          src={logoUrl ?? noLogoUrl}
          css={{ borderRadius: 4, width: 120 }}
        />
      )
    },
    { cell: "name", label: "Name" },
    { cell: "timeSummary", label: "Date" },
    { cell: "attendeeCounts.activeCount", label: "Attendees" },
    { cell: "visibility", label: "Visibility" },
    {
      cell: "registration",
      label: "Registration",
      render: EventStatusIndicator
    },
    {
      cell: "mostRecentRegistration",
      label: "Most Recent",
      render: ({ mostRecentRegistration }) =>
        mostRecentRegistration ? formatDateTimeShort(mostRecentRegistration) : <span>&mdash;</span>
    },
    {
      cell: "ccwLink",
      render: props => (
        <ViewOnChurchCenterButton
          {...props}
          ccwRegistrationsPublished={ccwRegistrationsPublished}
          ccwEnabled={ccwEnabled}
        />
      )
    }
  ]

  const cardStyles = {
    padding: 0,
    "@media (min-width: 600px)": {
      width: "calc((100% - 0.5rem) / 2)",
      height: "auto"
    },
    "@media (min-width: 960px)": {
      width: "calc((100% - 1rem) / 3)"
    }
  }

  const cardListWebStyles = {
    "@media (min-width: 600px)": {
      flexDirection: "row",
      flexWrap: "wrap"
    }
  }

  return orgHasEvents ? (
    <>
      <div>
        <EventsPageHeader
          isAdmin={isAdmin}
          churchCenterEventSort={churchCenterEventSort}
          churchCenterEventsUrl={churchCenterEventsUrl}
          churchCenterDisabledOrUnpublished={churchCenterDisabledOrUnpublished}
          ccaEnabled={ccaEnabled}
          ccwEnabled={ccwEnabled}
          ccaRegistrationsPublished={ccaRegistrationsPublished}
          ccwRegistrationsPublished={ccwRegistrationsPublished}
          subscribedToPublishing={subscribedToPublishing}
          isActingStaff={isActingStaff}
          isPhoenix={isPhoenix}
        />
        <StripeAccountNotificationBanner />
        {overageWarning && (
          <div className="overage-banner">
            <Icon size="lg" color="error-dark" name="general.exclamationCircle" />
            <span dangerouslySetInnerHTML={{ __html: overageWarning }} />
          </div>
        )}
      </div>
      <div className="page__content">
        <DynamicDataView
          heading={{
            useCounter: true,
            headerText: pluralize("active signup", "active signups", pagination.count),
            style: { padding: "12px" },
            headerTextStyle: { fontSize: "18px" }
          }}
          searchPlacement="aboveHeader"
          columns={columns}
          rows={events}
          onRowClick={handleRowClick}
          onCardClick={handleRowClick}
          scopeByFilters={[
            {
              name: "campus",
              initValue: campusFilter,
              options: [
                { label: "All campuses", value: "all" },
                ...campuses.map(campus => ({ label: campus.name, value: campus.id }))
              ]
            },
            {
              name: "category",
              initValue: archived ? "archived" : categoryFilter,
              options: [
                { label: "All categories", value: "all" },
                ...categories.map(category => ({ label: category.name, value: category.id })),
                { label: "Archived", value: "archived" }
              ]
            }
          ]}
          isLoading={isLoading}
          onScopeChange={handleFilter}
          onSearchChange={handleSearch}
          searchPlaceholder="Search"
          searchInitValue={searchQuery}
          primaryField={event => (
            <EventCard
              {...event}
              noLogoUrl={noLogoUrl}
              ccwEnabled={ccwEnabled}
              ccwRegistrationsPublished={ccwRegistrationsPublished}
            />
          )}
          cardStyles={cardStyles}
          cardListStyles={cardListWebStyles}
          emptyTableMessage="There are no signups that match your search query."
          emptyTableImageUrl="/static/blank-states/registrations-no-event.svg"
          onPaginateClick={handlePagination}
          pagination={pagination}
        />
      </div>
    </>
  ) : (
    <>
      <StripeAccountNotificationBanner />
      <EmptyEvents
        eventsCount={events.length}
        isAdmin={isAdmin}
        isOrgAdmin={isOrgAdmin}
        isStripeConnected={isStripeConnected}
        newEventUrl={newEventUrl}
        stripeConnectUrl={stripeConnectUrl}
        orgHasEvents={orgHasEvents}
      />
    </>
  )
}

Events.propTypes = {
  archived: bool,
  campuses: array,
  campusId: string,
  categories: array,
  categoryId: string,
  events: array,
  noLogoUrl: string,
  isAdmin: bool.isRequired,
  isOrgAdmin: bool.isRequired,
  isStripeConnected: bool.isRequired,
  stripeConnectUrl: string.isRequired,
  newEventUrl: string.isRequired,
  orgHasEvents: bool,
  churchCenterEventsUrl: string.isRequired,
  ccwEnabled: bool.isRequired,
  ccaEnabled: bool.isRequired,
  ccaRegistrationsPublished: bool.isRequired,
  ccwRegistrationsPublished: bool.isRequired,
  churchCenterDisabledOrUnpublished: bool,
  churchCenterEventSort: string,
  subscribedToPublishing: bool,
  pagination: object,
  overageWarning: string,
  isActingStaff: bool,
  isPhoenix: bool.isRequired
}

export default Events
